import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { Controller, Control } from "react-hook-form";
import { ttsGenerateUrl } from "../../../config";

type MediaType = 'image' | 'audio' | 'tts' | 'video';

interface MediaUploadProps {
    tabIndex?: number;
    viewMode?: boolean;
    editMode?: boolean;
    mediaType: MediaType | null | undefined;
    mediaFile: FileList | string | undefined;
    control: Control<any>;
    title?: string;
    language?: string;
    setSharedData: (data: any) => void;
    onMediaTypeChange: (type: MediaType) => void;
    onMediaFileChange: (file: FileList | null) => void;
    onUploadSuccess: (url: string, campaignId: string, fileName?: string) => void;
    onTtsGenerated?: (language: string, file: File) => void;
    onAudioFileChange?: (language: string, file: File) => void;
    onVideoFileChange?: (language: string, file: File) => void;
    onImageFileChange?: (language: string, file: File) => void;
    // Optional "secondary" image used with Audio/TTS (notification-style) - per language
    imageFile?: File | string | undefined;
    onImageFileChangeSecondary?: (language: string, file: File | null) => void;
    onError: (message: string) => void;
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
    tabIndex = 0,
    viewMode = false,
    editMode=false,
    mediaType,
    mediaFile,
    control,
    language = 'en',
    setSharedData,
    onMediaTypeChange,
    onMediaFileChange,
    onError,
    onTtsGenerated,
    onAudioFileChange,
    onVideoFileChange,
    onImageFileChange,
    imageFile,
    onImageFileChangeSecondary,
}) => {

    console.log("mediaFile>>",mediaFile);
    
    
    const normalizedMediaType: MediaType | null = (mediaType as MediaType) || null;
    const safeLang = language.toLowerCase();
    
    // Media state by language
    const [mediaByLanguage, setMediaByLanguage] = useState<Record<string, {
        file?: File;
        url?: string;
        label?: string;
        ttsText?: string;
    }>>({});
    
    // Store blob URLs per language for video (to prevent ERR_FILE_NOT_FOUND)
    const [videoBlobUrls, setVideoBlobUrls] = useState<Record<string, string>>({});
    // Store blob URLs per language for image (to prevent ERR_FILE_NOT_FOUND) - PER LANGUAGE
    const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
    
    const [isConvertingTts, setIsConvertingTts] = useState(false);
    const [currentTtsText, setCurrentTtsText] = useState('');
    const audioRef = useRef<HTMLAudioElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    // State for view mode audio/video/image URL
    const [viewModeAudioUrl, setViewModeAudioUrl] = useState<string | null>(null);
    const [viewModeVideoUrl, setViewModeVideoUrl] = useState<string | null>(null);
    const [viewModeImageUrl, setViewModeImageUrl] = useState<string | null>(null);
    // State for media type change confirmation dialog
    const [showMediaTypeChangeDialog, setShowMediaTypeChangeDialog] = useState(false);
    const [pendingMediaType, setPendingMediaType] = useState<MediaType | null>(null);
    const [pendingFieldOnChange, setPendingFieldOnChange] = useState<((value: MediaType) => void) | null>(null);

    // Get current media for active language
    const [currentMedia, setCurrentMedia] = useState<{
        file?: File;
        url?: string;
        label?: string;
        ttsText?: string;
    }>({});

    // Helper function to convert File to base64
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to convert file to base64'));
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Handle TTS conversion
    const handleConvertToSpeech = async () => {
        if (!currentTtsText.trim()) {
            onError("Please enter text to convert to speech");
            return;
        }

        try {
            setIsConvertingTts(true);

            const response = await fetch(ttsGenerateUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: currentTtsText, language }),
            });
            
            if (!response.ok) throw new Error("TTS request failed");

            const data = await response.json();
            if (!data?.data_url) throw new Error("Invalid TTS response");

            const generatedUrl = data.data_url;
            
            // Update state with TTS data
            setMediaByLanguage(prev => ({
                ...prev,
                [safeLang]: {
                    ...prev[safeLang],
                    url: generatedUrl,
                    label: `TTS Audio (${language})`,
                    ttsText: currentTtsText
                }
            }));

            // Create File from data URL
            const responseBlob = await fetch(generatedUrl);
            const audioBlob = await responseBlob.blob();
            const mimeType = generatedUrl.match(/data:([^;]+)/)?.[1] || 'audio/wav';
            const fileExt = mimeType.includes('wav') ? 'wav' : 'mp3';
            const file = new File([audioBlob], `tts_${safeLang}.${fileExt}`, { type: mimeType });

            if (onTtsGenerated) {
                onTtsGenerated(safeLang, file);
            }

        } catch (error) {
            console.error("TTS error:", error);
            onError("Failed to convert text to speech");
        } finally {
            setIsConvertingTts(false);
        }
    };

    // Handle file upload - IMAGES ARE NOW PER LANGUAGE
    const handleFileUpload = React.useCallback(async (files: FileList | null) => {
        if (!files || !files[0]) return;
        
        const file = files[0];
        
        if (normalizedMediaType === 'image' || normalizedMediaType === null) {
            // Image is per language (like video), store in current language key
            // Revoke previous blob URL for this language if exists
            if (imageBlobUrls[safeLang]) {
                URL.revokeObjectURL(imageBlobUrls[safeLang]);
            }
            const blobUrl = URL.createObjectURL(file);
            setImageBlobUrls(prev => ({
                ...prev,
                [safeLang]: blobUrl
            }));
            setMediaByLanguage(prev => ({
                ...prev,
                [safeLang]: { file, url: blobUrl, label: file.name } // Store image per language
            }));
            
            // Update currentMedia for image
            setCurrentMedia({
                file,
                url: blobUrl,
                label: file.name
            });
            
            // Notify parent component about image file change
            if (onImageFileChange) {
                onImageFileChange(safeLang, file);
            }
        } else if (normalizedMediaType === 'audio') {
            // Handle audio file upload per language - convert to base64
            try {
                const base64Url = await fileToBase64(file);
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { file, url: base64Url, label: file.name }
                }));
                
                setCurrentMedia(prev => ({
                    ...prev,
                    file,
                    url: base64Url,
                    label: file.name
                }));
                // Notify parent component about audio file change
                if (onAudioFileChange) {
                    onAudioFileChange(safeLang, file);
                }
            } catch (error) {
                console.error("Error converting audio to base64:", error);
                onError("Failed to process audio file");
                return;
            }
        } else if (normalizedMediaType === 'video') {
            // Handle video file upload per language - use blob URL for preview (not base64 for large files)
            try {
                // Revoke previous blob URL for this language if exists
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                }
                // Use blob URL instead of base64 for video to avoid memory issues with large files
                const blobUrl = URL.createObjectURL(file);
                // Store blob URL in state
                setVideoBlobUrls(prev => ({
                    ...prev,
                    [safeLang]: blobUrl
                }));
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { file, url: blobUrl, label: file.name }
                }));
                
                setCurrentMedia({
                    file,
                    url: blobUrl,
                    label: file.name
                });
                // Notify parent component about video file change
                if (onVideoFileChange) {
                    onVideoFileChange(safeLang, file);
                }
            } catch (error) {
                console.error("Error processing video file:", error);
                onError("Failed to process video file");
                return;
            }
        }
        
        onMediaFileChange(files);
    }, [normalizedMediaType, safeLang, onMediaFileChange, onAudioFileChange, onVideoFileChange, onImageFileChange, onError, imageBlobUrls, videoBlobUrls]);

    // Sync external mediaFile with internal state
    useEffect(() => {
        if (!mediaFile) return;
        
        if (typeof mediaFile === 'string') {
            const label = normalizedMediaType === 'audio' 
                ? 'Uploaded audio file' 
                : normalizedMediaType === 'video' 
                    ? 'Uploaded video file' 
                    : normalizedMediaType === 'image'
                        ? 'Uploaded image file'
                        : 'Uploaded file';
            setMediaByLanguage(prev => ({
                ...prev,
                [safeLang]: { ...prev[safeLang], url: mediaFile, label }
            }));
        } else if (mediaFile[0]) {
            // handleFileUpload(mediaFile);
        }
    }, [mediaFile, normalizedMediaType, safeLang, handleFileUpload]);

    // Load TTS text when language changes
    useEffect(() => {
        if (normalizedMediaType === 'tts') {
            setCurrentTtsText(currentMedia.ttsText || '');
        }
    }, [safeLang, normalizedMediaType, currentMedia.ttsText]);
    
    // Sync audio/video/image files from external source when in view mode or when mediaFile changes
    useEffect(() => {
        if ((normalizedMediaType === 'audio' || normalizedMediaType === 'video' || normalizedMediaType === 'image') && mediaFile) {
            if (typeof mediaFile === 'string') {
                const label = normalizedMediaType === 'audio' 
                    ? 'Uploaded audio file' 
                    : normalizedMediaType === 'video' 
                        ? 'Uploaded video file' 
                        : 'Uploaded image file';
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { 
                        ...prev[safeLang], 
                        url: mediaFile, 
                        label
                    }
                }));
                // Also update currentMedia immediately for view mode
                if (viewMode || editMode) {
                    setCurrentMedia(prev => ({
                        ...prev,
                        url: mediaFile,
                        label
                    }));
                    if (normalizedMediaType === 'audio') {
                        setViewModeAudioUrl(mediaFile);
                    } else if (normalizedMediaType === 'video') {
                        setViewModeVideoUrl(mediaFile);
                    } else if (normalizedMediaType === 'image') {
                        setViewModeImageUrl(mediaFile);
                    }
                }
            }
        }
    }, [mediaFile, normalizedMediaType, safeLang, viewMode,editMode]);

    // Reset audio player on tab/language change for TTS/Audio
    const previousAudioUrlRef = useRef<string | undefined>(undefined);
    
    useEffect(() => {
        if (normalizedMediaType !== 'tts' && normalizedMediaType !== 'audio') {
            previousAudioUrlRef.current = undefined;
            return;
        }
        
        const currentAudioUrl = currentMedia.url;
        
        if (previousAudioUrlRef.current === currentAudioUrl) {
            return;
        }
        
        previousAudioUrlRef.current = currentAudioUrl;
        
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            
            if (currentAudioUrl) {
                audioRef.current.src = currentAudioUrl;
                audioRef.current.load();
            } else {
                audioRef.current.src = '';
                audioRef.current.load();
            }
        }
    }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

    // Reset video player on tab/language change for Video
    const previousVideoUrlRef = useRef<string | undefined>(undefined);
    const previousLangRef = useRef<string | undefined>(undefined);
    
    useEffect(() => {
        if (normalizedMediaType !== 'video') {
            previousVideoUrlRef.current = undefined;
            previousLangRef.current = undefined;
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                videoRef.current.src = '';
                videoRef.current.removeAttribute('src');
                videoRef.current.load();
            }
            return;
        }
        
        const currentVideoUrl = currentMedia.url;
        const langChanged = previousLangRef.current !== safeLang;
        previousLangRef.current = safeLang;
        
        const shouldUpdate = langChanged || previousVideoUrlRef.current !== currentVideoUrl;
        
        if (!shouldUpdate) {
            return;
        }
        
        previousVideoUrlRef.current = currentVideoUrl;
        
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            
            if (currentVideoUrl) {
                videoRef.current.src = currentVideoUrl;
                videoRef.current.load();
            } else {
                videoRef.current.src = '';
                videoRef.current.removeAttribute('src');
                const sources = videoRef.current.querySelectorAll('source');
                sources.forEach(source => source.remove());
                videoRef.current.load();
            }
        }
    }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

    // Cleanup blob URLs only on component unmount
    useEffect(() => {
        return () => {
            Object.values(videoBlobUrls).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            Object.values(imageBlobUrls).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // For image type, check per language (like video)
        let mediaUrl: string | undefined;
        if (normalizedMediaType === 'image') {
            // Image is per language, always recreate blob URL from File object when switching tabs
            const storedFile = mediaByLanguage[safeLang]?.file;
            
            if (storedFile) {
                // Always recreate blob URL from File to ensure it's valid
                if (imageBlobUrls[safeLang]) {
                    URL.revokeObjectURL(imageBlobUrls[safeLang]);
                }
                const blobUrl = URL.createObjectURL(storedFile);
                setImageBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else if ((viewMode || editMode) && typeof mediaFile === 'string') {
                // In view mode, use mediaFile if it's a string URL
                mediaUrl = mediaFile;
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                // Create blob URL if not in state
                const blobUrl = URL.createObjectURL(mediaFile[0]);
                setImageBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else {
                // No image for this language - explicitly set to undefined
                if (imageBlobUrls[safeLang]) {
                    URL.revokeObjectURL(imageBlobUrls[safeLang]);
                    setImageBlobUrls(prev => {
                        const newState = { ...prev };
                        delete newState[safeLang];
                        return newState;
                    });
                }
                mediaUrl = undefined;
            }
        } else if (normalizedMediaType === 'video') {
            const storedFile = mediaByLanguage[safeLang]?.file;
            
            if (storedFile) {
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                }
                const blobUrl = URL.createObjectURL(storedFile);
                setVideoBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else if ((viewMode || editMode) && typeof mediaFile === 'string') {
                mediaUrl = mediaFile;
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                const blobUrl = URL.createObjectURL(mediaFile[0]);
                setVideoBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else {
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                    setVideoBlobUrls(prev => {
                        const newState = { ...prev };
                        delete newState[safeLang];
                        return newState;
                    });
                }
                mediaUrl = undefined;
            }
        } else {
            // For other types (audio, tts), check per language
            mediaUrl = mediaByLanguage[safeLang]?.url || ((viewMode || editMode) && typeof mediaFile === 'string' ? mediaFile : undefined);
        }
        
        if(mediaUrl) {   
            let mediaData;
            if (normalizedMediaType === 'image') {
                // For image, set mediaData if we have a file for this language OR if in view mode with a URL
                if (mediaByLanguage[safeLang]?.file) {
                    mediaData = { ...mediaByLanguage[safeLang], url: mediaUrl };
                } else if ((viewMode || editMode) && typeof mediaFile === 'string' && mediaUrl) {
                    mediaData = { url: mediaUrl, label: 'Uploaded image' };
                } else {
                    mediaData = { file: undefined, url: undefined, label: undefined, ttsText: undefined };
                }
            } else if (normalizedMediaType === 'video') {
                if (mediaByLanguage[safeLang]?.file) {
                    mediaData = { ...mediaByLanguage[safeLang], url: mediaUrl };
                } else if ((viewMode || editMode) && typeof mediaFile === 'string' && mediaUrl) {
                    mediaData = { url: mediaUrl, label: 'Uploaded video' };
                } else {
                    mediaData = { file: undefined, url: undefined, label: undefined, ttsText: undefined };
                }
            } else {
                mediaData = mediaByLanguage[safeLang] || { url: mediaUrl, label: 'Uploaded file' };
            }
            setCurrentMedia(mediaData);
            if (normalizedMediaType === 'audio' || normalizedMediaType === 'tts') {
                setViewModeAudioUrl(mediaUrl);
            } else if (normalizedMediaType === 'video') {
                setViewModeVideoUrl(mediaUrl);
            } else if (normalizedMediaType === 'image') {
                setViewModeImageUrl(mediaUrl);
            }
        }else{
            // Clear current media
            setCurrentMedia({
                file: undefined,
                url: undefined,
                label: undefined,
                ttsText: undefined
            });
            
            if (normalizedMediaType === 'video' && videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                videoRef.current.src = '';
                videoRef.current.load();
            }
            
            setSharedData((prev:any) => ({
                ...prev,
                mediaFile: undefined,
            }));

            setViewModeAudioUrl(null);
            setViewModeVideoUrl(null);
            setViewModeImageUrl(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIndex, normalizedMediaType, safeLang, mediaByLanguage, mediaFile, viewMode,editMode]);

    // Convert audio/video file to base64 in view mode
    useEffect(() => {
        if (viewMode && mediaFile && (normalizedMediaType === 'tts' || normalizedMediaType === 'audio')) {
            if (typeof mediaFile === 'string') {
                setViewModeAudioUrl(mediaFile);
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                fileToBase64(mediaFile[0])
                    .then(setViewModeAudioUrl)
                    .catch(error => {
                        console.error("Error converting audio to base64 in view mode:", error);
                        setViewModeAudioUrl(null);
                    });
            } else {
                setViewModeAudioUrl(null);
            }
        } else if (viewMode && normalizedMediaType === 'video') {
            // Handled in main useEffect
        } else if (viewMode && normalizedMediaType === 'image') {
            // Handled in main useEffect
        } else {
            if (!viewMode || (normalizedMediaType !== 'tts' && normalizedMediaType !== 'audio' && normalizedMediaType !== 'video' && normalizedMediaType !== 'image')) {
                setViewModeAudioUrl(null);
                setViewModeVideoUrl(null);
                setViewModeImageUrl(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, mediaFile, normalizedMediaType, safeLang, videoBlobUrls, imageBlobUrls]);

    const acceptTypes = normalizedMediaType === 'image' 
        ? 'image/*' 
        : normalizedMediaType === 'video' 
            ? 'video/*' 
            : 'audio/*';
    const buttonLabel = normalizedMediaType === 'image' 
        ? 'Upload Image' 
        : normalizedMediaType === 'video' 
            ? 'Upload Video' 
            : 'Upload Audio';

    // View Mode
    if (viewMode) {
        const mediaUrl = typeof mediaFile === 'string' 
            ? mediaFile 
            : normalizedMediaType === 'image' && mediaFile
                ? URL.createObjectURL(mediaFile[0])
                : viewModeAudioUrl || viewModeImageUrl || '';

        return (
            <Box mt={2}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Select Media Type</FormLabel>
                    <Controller
                        name="mediaType"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup
                                row
                                value={mediaType || ''}
                            >
                                <FormControlLabel 
                                    value="image" 
                                    control={<Radio disabled={true} />} 
                                    label="Image" 
                                />
                                <FormControlLabel 
                                    value="audio" 
                                    control={<Radio disabled={true} />} 
                                    label="Audio" 
                                />
                                <FormControlLabel 
                                    value="video" 
                                    control={<Radio disabled={true} />} 
                                    label="Video" 
                                />
                                <FormControlLabel 
                                    value="tts" 
                                    control={<Radio disabled={true} />} 
                                    label="TTS" 
                                />
                            </RadioGroup>
                        )}
                    />
                </FormControl>

                {mediaFile && (
                    <>
                        <Typography variant="subtitle2" mt={2}>
                            Uploaded {normalizedMediaType === 'tts' || normalizedMediaType === 'audio' 
                                ? 'Audio' 
                                : normalizedMediaType === 'video' 
                                    ? 'Video' 
                                    : normalizedMediaType}
                        </Typography>
                        <Box mt={1}>
                            {normalizedMediaType === 'image' && (viewModeImageUrl || mediaUrl) && (
                                <img
                                    src={viewModeImageUrl || mediaUrl}
                                    alt="Uploaded"
                                    style={{width: '50%', height: '50%', borderRadius: 8 }}
                                />
                            )}
                            {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && viewModeAudioUrl && (
                                <Box>
                                    {/* Audio first, then image below */}
                                    <audio
                                        controls
                                        style={{ width: '100%', maxWidth: '500px', marginBottom: imageFile ? 16 : 0 }}
                                        preload="metadata"
                                        key={`audio-${safeLang}-${tabIndex}-${normalizedMediaType}-${currentMedia.url || 'empty'}`}
                                    >
                                        <source src={viewModeAudioUrl} type="audio/mpeg" />
                                        <source src={viewModeAudioUrl} type="audio/wav" />
                                        <source src={viewModeAudioUrl} type="audio/mp3" />
                                        Your browser does not support the audio element.
                                    </audio>
                                    {/* Image below audio if exists */}
                                    {imageFile && (
                                        <Box mt={2}>
                                            {typeof imageFile === 'string' ? (
                                                <img
                                                    src={imageFile}
                                                    alt="Uploaded"
                                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                                                />
                                            ) : imageFile instanceof File ? (
                                                <img
                                                    src={URL.createObjectURL(imageFile)}
                                                    alt="Uploaded"
                                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                                                />
                                            ) : null}
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {normalizedMediaType === 'video' && viewModeVideoUrl && (
                                <video
                                    ref={videoRef}
                                    controls
                                    style={{ width: '100%', maxWidth: '500px', borderRadius: 8 }}
                                    key={`video-view-${safeLang}-${viewModeVideoUrl}`}
                                    preload="metadata"
                                >
                                    <source src={viewModeVideoUrl} type="video/mp4" />
                                    <source src={viewModeVideoUrl} type="video/webm" />
                                    <source src={viewModeVideoUrl} type="video/ogg" />
                                    Your browser does not support the video element.
                                </video>
                            )}
                        </Box>
                    </>
                )}

                {/* Note: Image for TTS/Audio is now shown together with audio above, so we don't need a separate section here */}
            </Box>
        );
    }

    // Edit Mode
    return (
        <Box mt={2}>
            <FormControl component="fieldset">
                <FormLabel component="legend">Select Media Type</FormLabel>
                <Controller
                    name="mediaType"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup
                            row
                            value={mediaType || ''}
                            onChange={(e) => {
                                if (viewMode) return;
                                
                                const newType = e.target.value as MediaType;
                                
                                const hasUploadedMedia = 
                                    Object.keys(mediaByLanguage).some(
                                        lang => mediaByLanguage[lang]?.file || mediaByLanguage[lang]?.url
                                    ) || 
                                    currentMedia.file || currentMedia.url ||
                                    (mediaFile && (typeof mediaFile === 'string' || (mediaFile instanceof FileList && mediaFile.length > 0))) ||
                                    (normalizedMediaType === 'tts' && currentTtsText.trim()) ||
                                    (imageFile && (typeof imageFile === 'string' || (imageFile instanceof FileList && imageFile.length > 0)));
                                
                                if (hasUploadedMedia && normalizedMediaType !== newType) {
                                    console.log("changed media type >>",newType);
                                    setPendingMediaType(newType);
                                    setPendingFieldOnChange(() => field.onChange);
                                    setShowMediaTypeChangeDialog(true);
                                } else {
                                    console.log("changed media type 2 >>",newType);
                                    field.onChange(newType);
                                    onMediaTypeChange(newType);
                                }
                            }}
                        >
                            <FormControlLabel 
                                value="image" 
                                control={<Radio disabled={viewMode} />} 
                                label="Image" 
                            />
                            <FormControlLabel 
                                value="audio" 
                                control={<Radio disabled={viewMode} />} 
                                label="Audio" 
                            />
                            <FormControlLabel 
                                value="video" 
                                control={<Radio disabled={viewMode} />} 
                                label="Video" 
                            />
                            <FormControlLabel 
                                value="tts" 
                                control={<Radio disabled={viewMode} />} 
                                label="TTS" 
                            />
                        </RadioGroup>
                    )}
                />
            </FormControl>

            <Dialog open={showMediaTypeChangeDialog} onClose={() => setShowMediaTypeChangeDialog(false)}>
                <DialogTitle>Confirm Media Type Change</DialogTitle>
                <DialogContent>
                    <Typography>
                        Your uploaded media will be lost. Are you sure you want to change the media type?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowMediaTypeChangeDialog(false);
                        setPendingMediaType(null);
                        setPendingFieldOnChange(null);
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => {
                            if (pendingMediaType !== null && pendingFieldOnChange) {
                                Object.values(videoBlobUrls).forEach(url => {
                                    if (url?.startsWith('blob:')) {
                                        URL.revokeObjectURL(url);
                                    }
                                });
                                Object.values(imageBlobUrls).forEach(url => {
                                    if (url?.startsWith('blob:')) {
                                        URL.revokeObjectURL(url);
                                    }
                                });
                                setMediaByLanguage({});
                                setVideoBlobUrls({});
                                setImageBlobUrls({});
                                setCurrentMedia({});
                                setCurrentTtsText('');
                                setViewModeAudioUrl(null);
                                setViewModeVideoUrl(null);
                                setViewModeImageUrl(null);
                                
                                onMediaFileChange(null);
                                if (onImageFileChangeSecondary) {
                                    // Clear secondary image for current language
                                    onImageFileChangeSecondary(safeLang, null);
                                }
                                
                                pendingFieldOnChange(pendingMediaType);
                                onMediaTypeChange(pendingMediaType);
                                
                                setShowMediaTypeChangeDialog(false);
                                setPendingMediaType(null);
                                setPendingFieldOnChange(null);
                            }
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {normalizedMediaType === 'tts' && (
                <Box mt={2}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Enter text to convert to speech"
                        value={currentTtsText}
                        onChange={(e) => {
                            const newText = e.target.value;
                            setCurrentTtsText(newText);
                            setMediaByLanguage(prev => ({
                                ...prev,
                                [safeLang]: { 
                                    ...prev[safeLang], 
                                    ttsText: newText,
                                    url: undefined,
                                    label: undefined
                                }
                            }));
                        }}
                        placeholder="Type your text here..."
                        sx={{ mb: 2 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleConvertToSpeech}
                        disabled={!currentTtsText.trim() || isConvertingTts}
                        sx={{ mb: 2 }}
                    >
                        {isConvertingTts ? "Converting..." : "Convert to Speech"}
                    </Button>
                </Box>
            )}

            {/* File Upload - Images are now per language */}
            {(normalizedMediaType === 'image' || normalizedMediaType === 'audio') && (
                <Controller
                    name="mediaFile"
                    control={control}
                    render={({ field }) => (
                        <Box display="flex" alignItems="center" gap={2} mb={2} mt={2}>
                            <Button variant="outlined" component="label">
                                {buttonLabel}
                                <input
                                    type="file"
                                    accept={acceptTypes}
                                    hidden
                                    onChange={e => {
                                        field.onChange(e.target.files);
                                        handleFileUpload(e.target.files);
                                    }}
                                />
                            </Button>
                            {normalizedMediaType === 'image' && (
                                <Typography color="error">*</Typography>
                            )}
                            {currentMedia.label && (
                                <Typography variant="body2">{currentMedia.label}</Typography>
                            )}
                        </Box>
                    )}
                />
            )}
            
            {normalizedMediaType === 'video' && (
                <Controller
                    name="mediaFile"
                    control={control}
                    render={({ field }) => (
                        <Box display="flex" alignItems="center" gap={2} mb={2} mt={2}>
                            <Button variant="outlined" component="label">
                                Upload Video
                                <input
                                    type="file"
                                    accept="video/*"
                                    hidden
                                    onChange={e => {
                                        field.onChange(e.target.files);
                                        handleFileUpload(e.target.files);
                                    }}
                                />
                            </Button>
                            {currentMedia.label && (
                                <Typography variant="body2">{currentMedia.label}</Typography>
                            )}
                        </Box>
                    )}
                />
            )}

            {/* Image Upload for Audio/TTS (notification-style) - per language */}
            {onImageFileChangeSecondary && (normalizedMediaType === 'audio' || normalizedMediaType === 'tts') && (
                <Box mt={2}>
                    <Button variant="outlined" component="label">
                        Upload Image
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={e => {
                                if (e.target.files && e.target.files[0] && onImageFileChangeSecondary) {
                                    onImageFileChangeSecondary(safeLang, e.target.files[0]);
                                }
                            }}
                        />
                    </Button>
                    {imageFile && (
                        <>
                            <Typography variant="body2" mt={1}>
                                {typeof imageFile === 'string' ? imageFile : imageFile.name}
                            </Typography>
                            {typeof imageFile !== 'string' && imageFile instanceof File && (
                                <img
                                    src={URL.createObjectURL(imageFile)}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 8, marginTop: 8 }}
                                />
                            )}
                        </>
                    )}
                </Box>
            )}

            {currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="subtitle2" mb={1}>Preview:</Typography>
                    
                    {normalizedMediaType === 'image' && (
                        <img
                            src={currentMedia.url}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: 8 }}
                        />
                    )}
                    {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && currentMedia.url && (
                        <Box>
                            {currentMedia.label && <Typography variant="body2" mb={1}>{currentMedia.label}</Typography>}
                            {/* Audio first, then image below */}
                            <audio
                                ref={audioRef}
                                controls
                                style={{ width: '100%', maxWidth: '500px', marginBottom: imageFile ? 16 : 0 }}
                                key={`audio-${safeLang}-${tabIndex}-${normalizedMediaType}-${currentMedia.url || 'empty'}`}
                                preload="metadata"
                            >
                                <source src={currentMedia.url} type="audio/mpeg" />
                                <source src={currentMedia.url} type="audio/wav" />
                                <source src={currentMedia.url} type="audio/mp3" />
                                Your browser does not support the audio element.
                            </audio>
                            {/* Image below audio if exists */}
                            {imageFile && (
                                <Box mt={2}>
                                    {typeof imageFile === 'string' ? (
                                        <img
                                            src={imageFile}
                                            alt="Uploaded"
                                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                                        />
                                    ) : imageFile instanceof File ? (
                                        <img
                                            src={URL.createObjectURL(imageFile)}
                                            alt="Uploaded"
                                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                                        />
                                    ) : null}
                                </Box>
                            )}
                        </Box>
                    )}

                    {normalizedMediaType === 'video' && currentMedia.url  && (
                        <Box>
                            {currentMedia.label && <Typography variant="body2" mb={1}>{currentMedia.label}</Typography>}
                            <video
                                ref={videoRef}
                                controls
                                style={{ width: '100%', maxWidth: '500px', borderRadius: 8 }}
                                key={`video-${safeLang}-${tabIndex}-${currentMedia.url || 'empty'}`}
                                preload="metadata"
                            >
                                <source src={currentMedia.url} type="video/mp4" />
                                <source src={currentMedia.url} type="video/webm" />
                                <source src={currentMedia.url} type="video/ogg" />
                                Your browser does not support the video element.
                            </video>
                        </Box>
                    )}
                </Box>
            )}
            
            {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && !currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No audio uploaded for {language.toUpperCase()} language yet.
                    </Typography>
                </Box>
            )}
            {normalizedMediaType === 'video' && !currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No video uploaded for {language.toUpperCase()} language yet.
                    </Typography>
                </Box>
            )}
            {normalizedMediaType === 'image' && !currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No image uploaded for {language.toUpperCase()} language yet.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
